import { useState, useEffect, useMemo, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { BookCondition, BookStatus } from "@passa-a-pagina/shared";
import { isValidIsbn } from "@passa-a-pagina/shared";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { BookUploadModal } from "../../components/BookUploadModal";
import "./books.css";

interface AdminBook {
  id: string;
  isbn: string | null;
  title: string;
  author: string | null;
  coverUrl: string | null;
  genre: string | null;
  condition: BookCondition;
  status: BookStatus;
  donorEmail: string | null;
  notes: string | null;
  removedAt: string | null;
  scannedAt: string;
}

interface ListResponse {
  items: AdminBook[];
  total: number;
  page: number;
  pages: number;
}

const VALID_STATUSES: BookStatus[] = ["available", "on_hold", "checked_out"];

export function AdminBooks() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  // URL search params drive initial filter state so the page can be linked
  // to from Stats cards or anywhere else. Filters stay in component state
  // once mounted — we don't aggressively round-trip every change back to
  // the URL (avoids history pollution while typing in the search box).
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (() => {
    const raw = searchParams.get("status");
    return raw && (VALID_STATUSES as string[]).includes(raw) ? (raw as BookStatus) : "";
  })();
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [submittedQ, setSubmittedQ] = useState(searchParams.get("q") ?? "");
  const [status, setStatus] = useState<BookStatus | "">(initialStatus);
  const [includeRemoved, setIncludeRemoved] = useState(
    searchParams.get("includeRemoved") === "true",
  );
  const [page, setPage] = useState(Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1));
  const [editing, setEditing] = useState<AdminBook | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<AdminBook | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; msg: string } | null>(null);
  const [exporting, setExporting] = useState(false);

  // Mirror the active filters back to the URL on a coarse cadence (submit /
  // status change / page change) — keeps URLs shareable without thrashing
  // history on every keystroke.
  useEffect(() => {
    const next = new URLSearchParams();
    if (submittedQ) next.set("q", submittedQ);
    if (status) next.set("status", status);
    if (includeRemoved) next.set("includeRemoved", "true");
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [submittedQ, status, includeRemoved, page, setSearchParams]);

  const params = useMemo(() => {
    const sp = new URLSearchParams();
    if (submittedQ) sp.set("q", submittedQ);
    if (status) sp.set("status", status);
    if (includeRemoved) sp.set("includeRemoved", "true");
    sp.set("page", String(page));
    sp.set("limit", "30");
    return sp.toString();
  }, [submittedQ, status, includeRemoved, page]);

  const list = useQuery<ListResponse, ApiError>({
    queryKey: ["admin", "books-list", params],
    queryFn: () => api<ListResponse>(`/api/admin/books?${params}`),
    retry: false,
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; body: Partial<AdminBook> }) =>
      api<{ ok: true }>(`/api/admin/books/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: () => {
      setFlash({ type: "ok", msg: t("admin.books.updated") });
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin", "books-list"] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/admin/books/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setFlash({ type: "ok", msg: t("admin.books.removed") });
      setConfirmRemove(null);
      qc.invalidateQueries({ queryKey: ["admin", "books-list"] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  if (loading) return <div className="container"><p>{t("common.loading")}</p></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setPage(1);
    setSubmittedQ(q.trim());
  }

  async function onExport() {
    setExporting(true);
    setFlash(null);
    try {
      // Export the current view (same filters as the list), all pages. Auth is
      // cookie-based and same-origin, so a credentialed fetch carries it; we
      // stream the xlsx into a Blob and trigger a download.
      const sp = new URLSearchParams();
      if (submittedQ) sp.set("q", submittedQ);
      if (status) sp.set("status", status);
      if (includeRemoved) sp.set("includeRemoved", "true");
      const res = await fetch(`/api/admin/books/export?${sp.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalogo-passa-a-pagina-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setFlash({ type: "err", msg: t("admin.books.exportError") });
    } finally {
      setExporting(false);
    }
  }

  return (
    <section aria-labelledby="books-title" className="admin__panel books">
      <header className="books__header">
        <h2 id="books-title">{t("admin.books.catalogue")}</h2>
        <div className="books__header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onExport}
            disabled={exporting}
          >
            {exporting ? t("admin.books.exporting") : t("admin.books.exportExcel")}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setUploadOpen(true)}
          >
            {t("admin.books.importExcel")}
          </button>
        </div>
      </header>

      {flash && (
        <div
          className={`books__flash books__flash--${flash.type}`}
          role="alert"
          aria-live="polite"
        >
          {flash.msg}
        </div>
      )}

      <form onSubmit={onSearch} className="surface books__filters">
        <div className="field">
          <label htmlFor="books-q">{t("admin.books.search")}</label>
          <input
            id="books-q"
            type="search"
            placeholder={t("admin.books.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="books-status">{t("admin.books.statusLabel")}</label>
          <select
            id="books-status"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as BookStatus | "");
            }}
          >
            <option value="">{t("admin.books.statusAll")}</option>
            <option value="available">{t("admin.books.status.available")}</option>
            <option value="on_hold">{t("admin.books.status.on_hold")}</option>
            <option value="checked_out">{t("admin.books.status.checked_out")}</option>
          </select>
        </div>
        <label className="books__check">
          <input
            type="checkbox"
            checked={includeRemoved}
            onChange={(e) => {
              setPage(1);
              setIncludeRemoved(e.target.checked);
            }}
          />
          <span>{t("admin.books.showRemoved")}</span>
        </label>
        <button type="submit" className="btn btn-primary">{t("admin.books.filter")}</button>
      </form>

      {list.isLoading ? (
        <p>{t("common.loading")}</p>
      ) : list.error ? (
        <p className="field__error" role="alert">{list.error.message}</p>
      ) : list.data ? (
        <>
          <p className="books__count">
            {t("admin.books.count", { count: list.data.total })}
          </p>
          <div className="books__table-wrap">
            <table className="books__table">
              <thead>
                <tr>
                  <th scope="col">{t("admin.books.colCover")}</th>
                  <th scope="col">{t("admin.books.colTitle")}</th>
                  <th scope="col">{t("admin.books.colAuthor")}</th>
                  <th scope="col">{t("admin.books.colGenre")}</th>
                  <th scope="col">{t("admin.books.colCondition")}</th>
                  <th scope="col">{t("admin.books.colStatus")}</th>
                  <th scope="col">{t("admin.books.colNotes")}</th>
                  <th scope="col">{t("admin.books.colDonor")}</th>
                  <th scope="col">{t("admin.books.colEntry")}</th>
                  <th scope="col">{t("admin.books.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((b) => (
                  <tr key={b.id} className={b.removedAt ? "books__row--removed" : ""}>
                    <td>
                      {b.coverUrl ? (
                        <img src={`/api/cover/${b.id}`} alt="" className="books__cover" loading="lazy" />
                      ) : (
                        <div className="books__cover books__cover--empty" aria-hidden="true">📖</div>
                      )}
                    </td>
                    <td className="books__title-cell">
                      {b.title}
                      {b.removedAt && <span className="books__removed-badge">{t("admin.books.removedBadge")}</span>}
                      {b.isbn && <small><code>{b.isbn}</code></small>}
                    </td>
                    <td>{b.author ?? "—"}</td>
                    <td>{b.genre ?? "—"}</td>
                    <td>{t(`admin.books.condition.${b.condition}`)}</td>
                    <td>{t(`admin.books.status.${b.status}`)}</td>
                    <td className="books__notes">{b.notes ?? "—"}</td>
                    <td className="books__donor">{b.donorEmail ?? <em>{t("admin.books.anon")}</em>}</td>
                    <td>{new Date(b.scannedAt).toLocaleDateString(i18n.language || "pt-PT")}</td>
                    <td className="books__actions">
                      {!b.removedAt && (
                        <>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setEditing(b)}
                          >
                            {t("admin.books.edit")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => setConfirmRemove(b)}
                            disabled={b.status === "on_hold"}
                            title={b.status === "on_hold" ? t("admin.books.releaseHoldFirst") : ""}
                          >
                            {t("admin.books.remove")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {list.data.items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="books__empty">
                      {t("admin.books.noResults")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {list.data.pages > 1 && (
            <nav className="books__pagination" aria-label={t("admin.books.pagination")}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("admin.books.prev")}
              </button>
              <span className="books__page-info">
                {t("admin.books.pageInfo", { page: list.data.page, pages: list.data.pages })}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= list.data.pages}
                onClick={() => setPage((p) => Math.min(list.data!.pages, p + 1))}
              >
                {t("admin.books.next")}
              </button>
            </nav>
          )}
        </>
      ) : null}

      {editing && (
        <EditModal
          book={editing}
          onClose={() => setEditing(null)}
          onSave={(body) => patch.mutate({ id: editing.id, body })}
          saving={patch.isPending}
        />
      )}

      {confirmRemove && (
        <ConfirmModal
          title={t("admin.books.removeTitle")}
          body={t("admin.books.removeBody", { title: confirmRemove.title })}
          confirmLabel={t("admin.books.remove")}
          onConfirm={() => remove.mutate(confirmRemove.id)}
          onCancel={() => setConfirmRemove(null)}
          working={remove.isPending}
        />
      )}

      {uploadOpen && <BookUploadModal onClose={() => setUploadOpen(false)} />}
    </section>
  );
}

function EditModal({
  book,
  onClose,
  onSave,
  saving,
}: {
  book: AdminBook;
  onClose: () => void;
  onSave: (body: Partial<AdminBook>) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(book.title);
  const [isbn, setIsbn] = useState(book.isbn ?? "");
  const [author, setAuthor] = useState(book.author ?? "");
  const [genre, setGenre] = useState(book.genre ?? "");
  const [condition, setCondition] = useState<BookCondition>(book.condition);
  const [notes, setNotes] = useState(book.notes ?? "");
  const [isbnError, setIsbnError] = useState<string | null>(null);
  const qc = useQueryClient();
  const [coverVersion, setCoverVersion] = useState(0);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverErr, setCoverErr] = useState<string | null>(null);

  async function uploadCover(f: File) {
    setCoverBusy(true);
    setCoverErr(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await fetch(`/api/admin/books/${book.id}/cover`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(d.error ?? `HTTP ${res.status}`);
      }
      setCoverVersion((v) => v + 1);
      qc.invalidateQueries({ queryKey: ["admin", "books-list"] });
    } catch (e) {
      setCoverErr((e as Error).message);
    } finally {
      setCoverBusy(false);
    }
  }

  async function removeCover() {
    setCoverBusy(true);
    setCoverErr(null);
    try {
      const res = await fetch(`/api/admin/books/${book.id}/cover`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(d.error ?? `HTTP ${res.status}`);
      }
      setCoverVersion((v) => v + 1);
      qc.invalidateQueries({ queryKey: ["admin", "books-list"] });
    } catch (e) {
      setCoverErr((e as Error).message);
    } finally {
      setCoverBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Empty clears the ISBN; otherwise it must be 10 or 13 digits (hyphens
    // and spaces are tolerated and stripped). Mirror the server-side rule so
    // the admin gets immediate feedback instead of a 400.
    const isbnClean = isbn.replace(/[-\s]/g, "").toUpperCase();
    if (isbnClean && !isValidIsbn(isbnClean)) {
      setIsbnError(t("admin.books.isbnInvalid"));
      return;
    }
    setIsbnError(null);
    onSave({
      title: title.trim(),
      isbn: isbnClean,
      author: author.trim() as string,
      genre: genre.trim() as string,
      condition,
      notes: notes.trim() as string,
    });
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="edit-title">
      <div className="modal">
        <h3 id="edit-title">{t("admin.books.editTitle")}</h3>
        <form onSubmit={onSubmit} className="modal__form">
          <div className="field">
            <span className="field__label-text">{t("admin.books.coverLabel")}</span>
            <div className="books__cover-edit">
              <img
                key={coverVersion}
                src={`/api/cover/${book.id}?v=${coverVersion}`}
                alt=""
                className="books__cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                }}
              />
              <div className="books__cover-actions">
                <label className={`btn btn-secondary btn-sm${coverBusy ? " is-disabled" : ""}`}>
                  {coverBusy ? t("admin.books.coverUploading") : t("admin.books.coverUpload")}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    disabled={coverBusy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadCover(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={removeCover}
                  disabled={coverBusy}
                >
                  {t("admin.books.coverRemove")}
                </button>
              </div>
            </div>
            {coverErr && <p className="field__error" role="alert">{coverErr}</p>}
            <p className="field__help">{t("admin.books.coverHelp")}</p>
          </div>
          <div className="field">
            <label htmlFor="edit-title-input">{t("admin.books.colTitle")}</label>
            <input
              id="edit-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="field">
            <label htmlFor="edit-isbn">{t("admin.books.fieldIsbn")}</label>
            <input
              id="edit-isbn"
              type="text"
              inputMode="numeric"
              value={isbn}
              onChange={(e) => {
                setIsbn(e.target.value);
                if (isbnError) setIsbnError(null);
              }}
              maxLength={20}
              placeholder={t("admin.books.isbnPlaceholder")}
              aria-invalid={Boolean(isbnError)}
              aria-describedby={isbnError ? "edit-isbn-error" : undefined}
            />
            {isbnError && (
              <p id="edit-isbn-error" className="field__error" role="alert">{isbnError}</p>
            )}
          </div>
          <div className="field">
            <label htmlFor="edit-author">{t("admin.books.colAuthor")}</label>
            <input
              id="edit-author"
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              maxLength={500}
            />
          </div>
          <div className="field">
            <label htmlFor="edit-genre">{t("admin.books.colGenre")}</label>
            <input
              id="edit-genre"
              type="text"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="field">
            <label htmlFor="edit-condition">{t("admin.books.colCondition")}</label>
            <select
              id="edit-condition"
              value={condition}
              onChange={(e) => setCondition(e.target.value as BookCondition)}
            >
              <option value="mint">{t("admin.books.condition.mint")}</option>
              <option value="good">{t("admin.books.condition.good")}</option>
              <option value="fair">{t("admin.books.condition.fair")}</option>
              <option value="worn">{t("admin.books.condition.worn")}</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="edit-notes">{t("admin.books.fieldNotes")}</label>
            <textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={3}
            />
          </div>
          <div className="modal__actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t("admin.books.saving") : t("admin.books.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  working,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  working: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="modal modal--small">
        <h3 id="confirm-title">{title}</h3>
        <p>{body}</p>
        <div className="modal__actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={working}>
            {t("common.cancel")}
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={working}>
            {working ? t("admin.books.processing") : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
